import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ModalPortal from '../Modal/ModalPortal';
import './ExitIntentModal.sass';

// Shown once per browser session on checkout pages when the cursor leaves the
// viewport toward the browser chrome (tab bar / close button) — the standard
// exit-intent signal. Browsers do not allow custom UI on actual tab close, so
// this fires on the movement that precedes it.
const STORAGE_KEY = 'postaway_exit_intent_shown';
const ARM_DELAY_MS = 3000;
const PROMO_CODE = 'ILOVEPOSTAWAY';

const alreadyShown = () => {
  try { return sessionStorage.getItem(STORAGE_KEY) === '1'; } catch (_) { return false; }
};
const markShown = () => {
  try { sessionStorage.setItem(STORAGE_KEY, '1'); } catch (_) {}
};

const ExitIntentModal = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const armedRef = useRef(false);

  useEffect(() => {
    // Delay arming so a stray cursor position on page load can't trigger it.
    const armTimer = setTimeout(() => { armedRef.current = true; }, ARM_DELAY_MS);

    const handleMouseOut = (e) => {
      if (!armedRef.current || alreadyShown()) return;
      // relatedTarget is null only when the cursor leaves the document
      // entirely; clientY <= 0 means it left through the top (toward the
      // close button / tab bar / URL bar).
      if (e.relatedTarget) return;
      if (e.clientY > 0) return;
      markShown();
      setIsOpen(true);
    };

    document.addEventListener('mouseout', handleMouseOut);
    return () => {
      clearTimeout(armTimer);
      document.removeEventListener('mouseout', handleMouseOut);
    };
  }, []);

  const handleSummitClick = () => {
    setIsOpen(false);
    navigate('/hospitality-summit');
  };

  if (!isOpen) return null;

  return (
    <ModalPortal isOpen={isOpen} onClose={() => setIsOpen(false)} maxWidth="640px">
      <div className="exit-intent-content">
        <img
          className="exit-intent-logo"
          src="/images/postaway_full_logo.png"
          alt="Postaway"
        />
        <h2 className="exit-intent-heading">Wait&hellip;you&rsquo;re leaving?</h2>
        <p className="exit-intent-hook">The algorithm isn&rsquo;t going to have your back.</p>
        <p className="exit-intent-hook-sub">But we will.</p>
        <p className="exit-intent-paragraph">
          Postaway is a hospitality community built to help you get seen, get connected and get
          supported&mdash;with people who actually want to share your business, collaborate and
          help you grow.
        </p>

        <button type="button" className="exit-intent-cta" onClick={() => setIsOpen(false)}>
          Join the Postaway Community
        </button>
        <button type="button" className="exit-intent-dismiss" onClick={() => setIsOpen(false)}>
          No thanks, I don&rsquo;t want my account to get seen by thousands of hospitality
          enthusiasts.
        </button>

        <p className="exit-intent-paragraph">
          Use code <span className="exit-intent-code">{PROMO_CODE}</span> at checkout for{' '}
          <strong>90% off your first year</strong> (less than $10/year) and let us earn your
          trust. We are obsessed with hospitality and want to show you exactly what that means.
        </p>

        <p className="exit-intent-paragraph">
          Lastly, we are also hosting a <strong>Postaway Hospitality Summit September 27-29 in
          Breckenridge, Colorado</strong> and would love for you to attend. This summit is the
          first of its kind including both hospitality creators and short-term rental hosts.
          It's going to be epic! Use code <span className="exit-intent-code">POSTAWAY15</span>{' '}
          at checkout.
        </p>
        <button type="button" className="exit-intent-summit-link" onClick={handleSummitClick}>
          www.postaway.com/hospitality-summit
        </button>

        <div className="exit-intent-signoff">
          <p>Until next time...Make Every Stay Count,<br />xoxo, Jamie</p>
          <p className="exit-intent-signature">
            Founder &amp; CEO, Postaway<br />
            www.postaway.com<br />
            @postawaytravels
          </p>
        </div>
      </div>
    </ModalPortal>
  );
};

export default ExitIntentModal;
